import React, { useEffect } from 'react';
import { useLayoutMetrics } from '../shared/hooks/useLayoutMetrics';

const IS_DEBUG_ENV =
  process.env.REACT_APP_ENV === 'stage' ||
  process.env.REACT_APP_ENV === 'development';

function useDebugLayoutParam(): boolean {
  const [active, setActive] = React.useState(() => {
    if (!IS_DEBUG_ENV) return false;
    return (
      new URLSearchParams(window.location.search).get('debug') === 'layout'
    );
  });

  useEffect(() => {
    if (!IS_DEBUG_ENV) return;

    function onPopState(): void {
      setActive(
        new URLSearchParams(window.location.search).get('debug') === 'layout'
      );
    }

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  return active;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: 16,
  right: 16,
  zIndex: 9999,
  backgroundColor: 'rgba(0, 0, 0, 0.82)',
  color: '#e2e8f0',
  fontFamily: 'monospace',
  fontSize: 11,
  lineHeight: 1.6,
  padding: '10px 14px',
  borderRadius: 6,
  minWidth: 220,
  pointerEvents: 'none',
  userSelect: 'none',
};

const labelStyle: React.CSSProperties = {
  color: '#a0aec0',
  marginRight: 6,
};

const valueStyle: React.CSSProperties = {
  color: '#68d391',
  fontWeight: 700,
};

const headerStyle: React.CSSProperties = {
  color: '#fc8181',
  fontWeight: 700,
  marginBottom: 4,
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
  fontSize: 10,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: 16,
};

/**
 * Dev/stage-only floating overlay that renders current layout metrics.
 *
 * Activated by the `?debug=layout` URL param.
 * Gated to `REACT_APP_ENV === 'development' | 'stage'` — never renders in prod.
 *
 * Reads metrics from the `useLayoutMetrics` hook (which uses ResizeObserver on
 * `data-layout-role` attributes). Also logs `console.table()` whenever metrics
 * change while the overlay is active.
 *
 * Mount once in the app root (see Task 1.4).
 */
export function DebugLayout(): React.ReactElement | null {
  const active = useDebugLayoutParam();
  const metrics = useLayoutMetrics();

  useEffect(() => {
    if (!active || metrics.timestamp === 0) return;

    console.table({
      'app-header': { height: metrics.headerHeight },
      'search-panel': { height: metrics.searchPanelHeight },
      'table-body': { height: metrics.tableBodyHeight },
      'visible-row-estimate': { height: metrics.visibleRowEstimate },
    });
  }, [active, metrics]);

  if (!IS_DEBUG_ENV || !active) return null;

  return (
    <div
      style={overlayStyle}
      aria-hidden="true"
      data-testid="debug-layout-overlay">
      <div style={headerStyle}>layout debug</div>
      <div style={rowStyle}>
        <span style={labelStyle}>app-header</span>
        <span style={valueStyle}>{metrics.headerHeight}px</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>search-panel</span>
        <span style={valueStyle}>{metrics.searchPanelHeight}px</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>table-body</span>
        <span style={valueStyle}>{metrics.tableBodyHeight}px</span>
      </div>
      <div style={rowStyle}>
        <span style={labelStyle}>visible rows ~</span>
        <span style={valueStyle}>{metrics.visibleRowEstimate}</span>
      </div>
    </div>
  );
}
